import { useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import BaseModal from '../../../../../components/modal/baseModal'
import { DynamicFormRef } from '../../../../../components/dynamicFormRef'
import { DynamicRefRef } from '../../../../../components/dynamicFormRef/types'
import { DinamicFormRefModel } from '../../../../../components/dynamicFormRef/types'
import { Item } from '../../../../../utils/types/generals'
import { icons } from '../../../../../utils/icons'
import { Step2SelectUsersProps } from '../types'
import { useStyles } from './step2SelectUsers.styles'
import { AvailableUser } from '../../createActionPlan/types'
import UserSelectionColumn from '../components/userSelectionColumn'

const mapItemsToIds = (items?: Item[]) =>
  (items ?? []).map((item) => Number(item.value)).filter(Boolean)

const Step2SelectUsers = ({
  data,
  errors,
  onChange,
  isCollaborator,
  users,
  filters,
}: Step2SelectUsersProps) => {
  const { t } = useTranslation()
  const styles = useStyles()

  const [assignedSearch, setAssignedSearch] = useState('')
  const [approverSearch, setApproverSearch] = useState('')

  const [assignedFilterOpen, setAssignedFilterOpen] = useState(false)
  const [approverFilterOpen, setApproverFilterOpen] = useState(false)

  const assignedFilterRef = useRef<DynamicRefRef | null>(null)
  const approverFilterRef = useRef<DynamicRefRef | null>(null)

  const [tempAssignedFilters, setTempAssignedFilters] = useState<Record<string, Item[]>>({})
  const [tempApproverFilters, setTempApproverFilters] = useState<Record<string, Item[]>>({})

  const {
    assigned: assignedUsers,
    approvers: approverUsers,
    isLoadingAssigned,
    isLoadingApprovers,
    assignedError,
    approversError,
  } = users

  const {
    options: filterOptions,
    isLoading: isLoadingFilters,
    appliedAssigned,
    appliedApprovers,
    onApplyAssigned,
    onApplyApprovers,
  } = filters

  const assignedFilterModel = useMemo<Record<string, DinamicFormRefModel>>(
    () => ({
      stores: {
        id: 'stores',
        type: 'multiSelect',
        label: t('actionPlan.manualCreation.step2.filters.unit'),
        name: 'stores',
        items: filterOptions.stores,
      },
      userTypes: {
        id: 'userTypes',
        type: 'multiSelect',
        label: t('actionPlan.manualCreation.step2.filters.userType'),
        name: 'userTypes',
        items: filterOptions.assignedUserTypes,
      },
      roles: {
        id: 'roles',
        type: 'multiSelect',
        label: t('actionPlan.manualCreation.step2.filters.role'),
        name: 'roles',
        items: filterOptions.assignedRoles,
      },
    }),
    [filterOptions.stores, filterOptions.assignedUserTypes, filterOptions.assignedRoles, t]
  )

  const approverFilterModel = useMemo<Record<string, DinamicFormRefModel>>(
    () => ({
      areas: {
        id: 'areas',
        type: 'multiSelect',
        label: t('actionPlan.manualCreation.step2.filters.area'),
        name: 'areas',
        items: filterOptions.areas,
        selectOptions: {
          child: {
            childKey: 'departments',
            parentId: 'areaId',
          },
        },
      },
      departments: {
        id: 'departments',
        type: 'multiSelect',
        label: t('actionPlan.manualCreation.step2.filters.department'),
        name: 'departments',
        items: filterOptions.departments,
        selectOptions: {
          parent: {
            parentKey: 'areas',
            parentId: 'areaId',
          },
        },
      },
    }),
    [filterOptions.areas, filterOptions.departments, t]
  )

  const filteredAssignedUsers = useMemo(() => {
    if (!assignedSearch.trim()) return assignedUsers
    const term = assignedSearch.toLowerCase()
    return assignedUsers.filter((user) => user.fullName.toLowerCase().includes(term))
  }, [assignedUsers, assignedSearch])

  const filteredApproverUsers = useMemo(() => {
    if (!approverSearch.trim()) return approverUsers
    const term = approverSearch.toLowerCase()
    return approverUsers.filter((user) => user.fullName.toLowerCase().includes(term))
  }, [approverUsers, approverSearch])

  const selectedAssignedId = data.assignedUser?.id ?? null
  const selectedApproverIds = useMemo(
    () => new Set(data.approvers.map((approver) => approver.id)),
    [data.approvers]
  )

  const handleSelectAssignedUser = (user: AvailableUser) => {
    onChange({ assignedUser: user })
  }

  const handleToggleApprover = (user: AvailableUser) => {
    const isSelected = data.approvers.some((approver) => approver.id === user.id)
    const newApprovers = isSelected
      ? data.approvers.filter((approver) => approver.id !== user.id)
      : [...data.approvers, user]
    onChange({ approvers: newApprovers })
  }

  const areAllApproversSelected = useMemo(
    () => filteredApproverUsers.length > 0 && filteredApproverUsers.every((user) =>
      data.approvers.some((approver) => approver.id === user.id)
    ),
    [filteredApproverUsers, data.approvers]
  )

  const handleSelectAllApprovers = () => {
    if (areAllApproversSelected) {
      const filteredIds = new Set(filteredApproverUsers.map((user) => user.id))
      onChange({
        approvers: data.approvers.filter((approver) => !filteredIds.has(approver.id)),
      })
      return
    }

    const currentIds = new Set(data.approvers.map((approver) => approver.id))
    const newApprovers = [
      ...data.approvers,
      ...filteredApproverUsers.filter((user) => !currentIds.has(user.id)),
    ]
    onChange({ approvers: newApprovers })
  }

  const handleOpenAssignedFilters = () => {
    setAssignedFilterOpen(true)
    setTempAssignedFilters(appliedAssigned)
    if (assignedFilterRef.current?.reset) {
      assignedFilterRef.current.reset(appliedAssigned)
    }
  }

  const handleOpenApproverFilters = () => {
    setApproverFilterOpen(true)
    setTempApproverFilters(appliedApprovers)
    if (approverFilterRef.current?.reset) {
      approverFilterRef.current.reset(appliedApprovers)
    }
  }

  const handleApplyAssignedFilters = () => {
    const values = ((assignedFilterRef.current?.getValues
      ? assignedFilterRef.current.getValues()
      : appliedAssigned) ?? appliedAssigned) as Record<string, Item[]>

    onApplyAssigned(
      values,
      {
        stores: mapItemsToIds(values.stores),
        userTypes: mapItemsToIds(values.userTypes),
        roles: mapItemsToIds(values.roles),
      }
    )
    setAssignedFilterOpen(false)
  }

  const handleApplyApproverFilters = () => {
    const values = ((approverFilterRef.current?.getValues
      ? approverFilterRef.current.getValues()
      : tempApproverFilters) ?? tempApproverFilters) as Record<string, Item[]>

    onApplyApprovers(
      values,
      {
        areas: mapItemsToIds(values.areas),
        departments: mapItemsToIds(values.departments),
      }
    )
    setApproverFilterOpen(false)
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>
          {t('actionPlan.manualCreation.step2.title')}
        </Typography>
        <Typography sx={styles.subtitle}>
          {t('actionPlan.manualCreation.step2.subtitle')}
        </Typography>
      </Box>

      <Box sx={styles.columnsContainer}>
        <UserSelectionColumn
          title={t('actionPlan.manualCreation.step2.assignedUser')}
          users={filteredAssignedUsers}
          isLoading={isLoadingAssigned}
          hasError={assignedError}
          searchValue={assignedSearch}
          onSearchChange={setAssignedSearch}
          selectionMode='single'
          selectedIds={selectedAssignedId !== null ? new Set([selectedAssignedId]) : new Set()}
          onSelectUser={handleSelectAssignedUser}
          error={errors.assignedUser}
          filterButton={
            <IconButton
              sx={styles.filterButton}
              onClick={handleOpenAssignedFilters}
              disabled={isLoadingFilters || isCollaborator}
            >
              {icons('filter')}
            </IconButton>
          }
        />

        <UserSelectionColumn
          title={t('actionPlan.manualCreation.step2.approvers')}
          users={filteredApproverUsers}
          isLoading={isLoadingApprovers}
          hasError={approversError}
          searchValue={approverSearch}
          onSearchChange={setApproverSearch}
          selectionMode='multiple'
          selectedIds={selectedApproverIds}
          onSelectUser={handleToggleApprover}
          onSelectAll={handleSelectAllApprovers}
          selectAllLabel={t('actionPlan.manualCreation.step2.selectAll')}
          error={errors.approvers}
          filterButton={
            <IconButton
              sx={styles.filterButton}
              onClick={handleOpenApproverFilters}
              disabled={isLoadingFilters}
            >
              {icons('filter')}
            </IconButton>
          }
        />
      </Box>

      <BaseModal
        sx={{ borderRadius: '0px' }}
        open={assignedFilterOpen}
      >
        <Box sx={styles.filterModalContainer}>
          <Box sx={styles.filterModalHeader}>
            <Typography sx={styles.filterModalTitle}>
              {t('actionPlan.manualCreation.step2.filters.assignedTitle')}
            </Typography>
            <IconButton
              onClick={() => setAssignedFilterOpen(false)}
              sx={styles.filterModalClose}
            >
              {icons('simple-close')}
            </IconButton>
          </Box>

          <DynamicFormRef
            ref={assignedFilterRef}
            model={assignedFilterModel}
            defaultValues={appliedAssigned}
            onChange={(values) => {
              setTempAssignedFilters(values as Record<string, Item[]>)
            }}
            parentWrapper={({ children }) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {children}
              </Box>
            )}
          />

          <Button
            variant='contained'
            onClick={handleApplyAssignedFilters}
            sx={styles.filterModalButton}
          >
            {t('actionPlan.manualCreation.step2.filters.filter')}
          </Button>
        </Box>
      </BaseModal>

      <BaseModal
        sx={{ borderRadius: '0px' }}
        open={approverFilterOpen}
      >
        <Box sx={styles.filterModalContainer}>
          <Box sx={styles.filterModalHeader}>
            <Typography sx={styles.filterModalTitle}>
              {t('actionPlan.manualCreation.step2.filters.approversTitle')}
            </Typography>
            <IconButton
              onClick={() => setApproverFilterOpen(false)}
              sx={styles.filterModalClose}
            >
              {icons('simple-close')}
            </IconButton>
          </Box>

          <DynamicFormRef
            ref={approverFilterRef}
            model={approverFilterModel}
            defaultValues={appliedApprovers}
            onChange={(values) => {
              setTempApproverFilters(values as Record<string, Item[]>)
            }}
            parentWrapper={({ children }) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {children}
              </Box>
            )}
          />

          <Button
            variant='contained'
            onClick={handleApplyApproverFilters}
            sx={styles.filterModalButton}
          >
            {t('actionPlan.manualCreation.step2.filters.filter')}
          </Button>
        </Box>
      </BaseModal>
    </Box>
  )
}

export default Step2SelectUsers
